import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../common/redux/hooks";
import { useNavigate } from "react-router-dom";
import {
  Currency,
  FrameworkAgreementDto,
  NeedDto,
  useGetFrameworkAgreementQuery,
  useStartCallOffForNeedMutation,
  useStartMiniCompetitionForNeedMutation,
} from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { displaySum } from "../../common/formatting/displaySum";
import { NumericInput } from "../../common/input/NumericInput";
import { urls } from "../../urls";
import {
  activePanel,
  byRank,
  defaultCallOffSupplier,
  remainingUnderCeiling,
  sharingBestRank,
} from "../framework-agreement/callOffAvailability";
import { procurementEntryHref } from "../sourcing-event/procurementEntryHref";
import { isNeedAlreadyStarted, reloadNeedIfStale } from "./staleNeed";
import { NeedAttachmentChoice } from "./NeedAttachmentChoice";
import { allowedForms, CallOffForm, needValueIn } from "./startFromFrameworkAgreement";

interface Props {
  need: NeedDto;
  agreements: FrameworkAgreementDto[];
  onClose: () => void;
}

export const StartFromFrameworkAgreementDialog = ({ need, agreements, onClose }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const displayer = useApiError();
  const [startMiniCompetition, { isLoading: isStartingMiniCompetition }] = useStartMiniCompetitionForNeedMutation();
  const [startCallOff, { isLoading: isStartingCallOff }] = useStartCallOffForNeedMutation();

  const [agreementId, setAgreementId] = useState(agreements.length === 1 ? agreements[0].id : "");
  const agreement = agreements.find((candidate) => candidate.id === agreementId);
  const forms = agreement ? allowedForms(t, agreement) : [];
  const [chosenForm, setForm] = useState<CallOffForm | null>(null);
  const form = chosenForm && forms.includes(chosenForm) ? chosenForm : (forms[0] ?? null);

  const members = agreement ? byRank(activePanel(agreement)) : [];
  const [chosenSupplier, setSupplier] = useState<string | null>(null);
  const supplier = chosenSupplier ?? defaultCallOffSupplier(members);
  const tiedAtTheTop = sharingBestRank(members);

  const currency = agreement?.ceilingValue?.currency ?? Currency.Nok;
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [chosenValue, setValue] = useState<number | null | undefined>(undefined);
  const value = chosenValue === undefined ? needValueIn(need, currency) : chosenValue;
  const {
    data: fullAgreement,
    isFetching: isLoadingCeiling,
    isError: ceilingFailed,
  } = useGetFrameworkAgreementQuery(
    { frameworkAgreementId: agreementId },
    { skip: !agreement },
  );
  const ceilingSource = fullAgreement?.id === agreementId ? fullAgreement : ceilingFailed ? agreement : undefined;
  const remaining = ceilingSource ? remainingUnderCeiling(ceilingSource) : null;
  const needValue = needValueIn(need, currency);
  const needOverCeiling = remaining !== null && needValue !== null && needValue > remaining;
  const overCeiling = remaining !== null && value !== null && value > remaining;
  const valueMissing = remaining !== null && value === null;

  const chooseAgreement = (id: string) => {
    setAgreementId(id);
    setForm(null);
    setSupplier(null);
    setValue(undefined);
  };

  const submit = async () => {
    if (!agreement || !form) return;
    try {
      if (form === "miniCompetition") {
        const linked = await startMiniCompetition({
          needId: need.id,
          startMiniCompetitionForNeedRequest: {
            frameworkAgreementId: agreement.id,
            attachmentIds: attachmentIds.filter((id) => (need.documents ?? []).some((document) => document.id === id)),
          },
        }).unwrap();
        onClose();
        if (linked.procurement) navigate(procurementEntryHref(linked.procurement.status, linked.procurement.id));
      } else {
        const linked = await startCallOff({
          needId: need.id,
          startCallOffForNeedRequest: {
            frameworkAgreementId: agreement.id,
            supplierOrganizationId: supplier,
            estimatedValue: value === null ? undefined : { value, currency },
          },
        }).unwrap();
        onClose();
        if (linked.callOff) navigate(urls.app.contracts.view.go(linked.callOff.id));
      }
    } catch (e) {
      displayer.trigger(e);
      reloadNeedIfStale(e, dispatch);
      if (isNeedAlreadyStarted(e)) onClose();
    }
  };

  const isDirectCallOff = form === "directCallOff";
  const canSubmit =
    !!agreement &&
    !!form &&
    !!ceilingSource &&
    !isLoadingCeiling &&
    (!isDirectCallOff || (supplier !== "" && !overCeiling && !valueMissing));

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="smModalBackground">
        <ModalHeader>{t("Use a framework agreement")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <FormControl>
              <FormLabel htmlFor="need-framework-agreement">{t("Framework agreement")}</FormLabel>
              <Select
                id="need-framework-agreement"
                backgroundColor="smBackground"
                value={agreementId}
                onChange={(e) => chooseAgreement(e.target.value)}
                placeholder={agreementId === "" ? (t("Choose the framework agreement") ?? undefined) : undefined}
              >
                {agreements.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.title}
                  </option>
                ))}
              </Select>
              <FormHelperText>
                {t("The need's title, description and estimated value go into what you start.")}
              </FormHelperText>
            </FormControl>

            {agreement && forms.length > 1 && (
              <FormControl>
                <FormLabel>{t("How")}</FormLabel>
                <RadioGroup value={form ?? ""} onChange={(next) => setForm(next as CallOffForm)}>
                  <Stack>
                    <Radio value="miniCompetition">
                      {t("Mini-competition among the suppliers on the framework agreement")}
                    </Radio>
                    <Radio value="directCallOff">{t("Direct call-off to one supplier")}</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
            )}

            {agreement && form === "miniCompetition" && (
              <Text fontSize="sm" color="smMuted">
                {t("{{count}} suppliers on the panel are invited.", { count: agreement.summary.supplierCount })}
              </Text>
            )}

            {agreement && form === "miniCompetition" && (
              <NeedAttachmentChoice
                attachments={need.documents ?? []}
                chosen={attachmentIds}
                onChange={setAttachmentIds}
              />
            )}

            {agreement && form === "miniCompetition" && needOverCeiling && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {t(
                  "The need's estimated value, {{value}}, is more than the {{remaining}} left under the framework agreement's ceiling value. The award has to fit under it.",
                  {
                    value: displaySum({ value: needValue ?? 0, currency }),
                    remaining: displaySum({ value: remaining ?? 0, currency }),
                  },
                )}
              </Alert>
            )}

            {agreement && isDirectCallOff && (
              <>
                <FormControl>
                  <FormLabel htmlFor="need-call-off-supplier">{t("Supplier")}</FormLabel>
                  <Select
                    id="need-call-off-supplier"
                    backgroundColor="smBackground"
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                    placeholder={supplier === "" ? (t("Choose the supplier") ?? undefined) : undefined}
                  >
                    {members.map((member) => (
                      <option key={member.organizationId} value={member.organizationId}>
                        {member.ranking ? `${member.ranking}. ${member.name}` : member.name}
                      </option>
                    ))}
                  </Select>
                  {tiedAtTheTop.length > 0 && (
                    <FormHelperText>
                      {t("{{suppliers}} share ranking {{rank}}. Choose which of them the call-off goes to.", {
                        suppliers: tiedAtTheTop.map((member) => member.name).join(", "),
                        rank: tiedAtTheTop[0].ranking,
                      })}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl isRequired={remaining !== null} isInvalid={overCeiling}>
                  <FormLabel htmlFor="need-call-off-value">{t("Estimated value")}</FormLabel>
                  <InputGroup maxW="280px">
                    <NumericInput
                      id="need-call-off-value"
                      backgroundColor="smBackground"
                      numericValue={value}
                      placeholder="0"
                      onNumericChange={setValue}
                    />
                    <InputRightAddon>{currency}</InputRightAddon>
                  </InputGroup>
                  {overCeiling ? (
                    <FormErrorMessage>
                      {t("More than the {{remaining}} left under the framework agreement's ceiling value.", {
                        remaining: displaySum({ value: remaining ?? 0, currency }),
                      })}
                    </FormErrorMessage>
                  ) : (
                    <FormHelperText>
                      {remaining === null
                        ? t("Excluding VAT")
                        : t("Excluding VAT. {{remaining}} left under the ceiling value.", {
                            remaining: displaySum({ value: remaining, currency }),
                          })}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            colorScheme="teal"
            isLoading={isStartingMiniCompetition || isStartingCallOff}
            isDisabled={!canSubmit}
            onClick={() => void submit()}
          >
            {isDirectCallOff ? t("Create call-off") : t("Create mini-competition")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
